import { MaterialIcons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { Linking, StyleSheet, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import Toast from 'react-native-toast-message';

import { GIFT_DISCOUNT_PERCENTAGE } from '@/constants/config';
import { BLUR_HASH } from '@/constants/layout';
import { applyDiscount } from '@/utils/applyDiscount';
import { firebaseImage, safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

import DeleteButton from '../buttons/DeleteButton';
import Text from '../Text';

interface GiftShowCardProps {
  showThumbnail: string;
  showPrice: string;
  firstName: string;
  lastName: string;
  phone: string;
  textColor?: string;
  showSms?: boolean;
  onDelete?: () => void;
}

const GiftShowCard = ({
  showThumbnail,
  showPrice,
  firstName,
  lastName,
  phone,
  textColor = 'white',
  showSms = false,
  onDelete
}: GiftShowCardProps) => {
  const { width } = useWindowDimensions();

  const handleSMS = async () => {
    const message = i18n.t('trb_show.gifted_sms_message');
    const smsUrl = `sms:${phone}?body=${encodeURIComponent(message)}`;

    try {
      const canOpen = await Linking.canOpenURL(smsUrl);

      if (canOpen) {
        await Linking.openURL(smsUrl);
      } else {
        Toast.show({
          type: 'error',
          text1: i18n.t('mix.sms_not_supported'),
          position: 'bottom',
          visibilityTime: 2000
        });
      }
    } catch (error) {
      console.error('Failed to send SMS:', error);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.firstRow}>
        {width >= 375 && (
          <Image
            placeholder={BLUR_HASH}
            style={styles.thumbnail}
            source={{ uri: firebaseImage(safeJsonParseArray(showThumbnail).at(0)) }}
            contentFit="cover"
          />
        )}
        <Text onPress={handleSMS} fontWeight="700" style={{ color: textColor }} numberOfLines={1}>
          {phone}
        </Text>
        <Text style={[{ color: textColor }, styles.flexOne]} numberOfLines={1}>{`${firstName} ${lastName}`}</Text>
      </View>
      <View style={styles.secondRow}>
        {showPrice !== undefined && (
          <Text fontWeight="900" style={{ color: textColor }}>
            {`$${applyDiscount(parseFloat(showPrice), GIFT_DISCOUNT_PERCENTAGE)}`}
          </Text>
        )}
        {onDelete !== undefined && <DeleteButton style={styles.deleteButton} color="white" onPress={onDelete} />}
        {showSms && (
          <TouchableOpacity style={styles.smsButton} onPress={handleSMS}>
            <View style={styles.smsButtonContainer}>
              <MaterialIcons name="textsms" size={18} color={textColor} />
              {width >= 768 && (
                <Text fontWeight="700" style={styles.textUser} numberOfLines={1}>
                  {i18n.t('mix.text_user')}
                </Text>
              )}
            </View>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  container: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  firstRow: {
    flex: 1,
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center'
  },

  secondRow: {
    flexDirection: 'row',
    gap: 5,
    marginLeft: 5,
    alignItems: 'center'
  },

  thumbnail: {
    height: 50,
    width: 75,
    borderRadius: 5
  },

  deleteButton: {
    padding: 5,
    backgroundColor: '#B41C1C',
    borderRadius: 5
  },

  smsButton: {
    padding: 5,
    backgroundColor: 'orange',
    borderRadius: 5
  },

  smsButtonContainer: {
    flexDirection: 'row',
    gap: 4
  },

  textUser: {
    color: 'black',
    fontSize: 16
  }
});

export default GiftShowCard;
